import { create } from 'zustand'
import type { SupportResponse, ChatMessage, HistoryEntry, AnalyticsEvent } from '../types'
import { addHistoryEntry, addAnalyticsEvent } from '../lib/db'
import { useUIStore } from './uiStore'

const API_URL = '/api/support'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

interface ChatState {
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  currentHistoryId: string | null

  sendMessage: (content: string) => Promise<void>
  clearChat: () => void
  loadFromHistory: (entry: HistoryEntry) => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  isLoading: false,
  error: null,
  currentHistoryId: null,

  sendMessage: async (content: string) => {
    if (!content.trim()) return

    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    set((state) => ({
      messages: [...state.messages, userMessage],
      isLoading: true,
      error: null,
    }))

    const startTime = performance.now()

    try {
      // Build chat history for Gemini (native format)
      const { messages } = get()
      const chatHistory = messages.map(m => ({
        role: m.role,
        content: m.content,
      }))

      // Call Cloudflare Worker endpoint with chat history
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: content,
          chatHistory, // Gemini SDK will handle multi-turn context
          activeTab: useUIStore.getState().activeTab, // Current platform tab context
        }),
      })

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`)
      }

      const data = await response.json()

      const responseTime = Math.round(performance.now() - startTime)

      // Check if this is a clarifying response or a final response
      const hasClarifyingQuestions = data.clarifyingQuestions && data.clarifyingQuestions.length > 0

      // Create assistant message
      let assistantContent = ''
      if (hasClarifyingQuestions) {
        assistantContent = data.clarifyingQuestions.join('\n\n')
        if (data.confidenceRationale) {
          assistantContent = `${data.confidenceRationale}\n\n${assistantContent}`
        }
      } else {
        // Use a summary for the chat, full response is in the response object
        assistantContent = `I've prepared responses for all platforms. Confidence: ${data.confidence}.\n\n${data.confidenceRationale || ''}`
      }

      // Parse infographic data with fallbacks
      const infographic = data.infographic || {}

      const supportResponse: SupportResponse = {
        tweet: data.tweet || '',
        facebook: data.facebook || '',
        forum: data.forum || '',
        infographic: {
          // New format (generated image)
          prompt: infographic.prompt,
          imageBase64: infographic.imageBase64,
          mimeType: infographic.mimeType,
          // Old format (concept card) - fallbacks
          title: infographic.title || 'Support Information',
          keyVisuals: infographic.keyVisuals || [],
          primaryTakeaway: infographic.primaryTakeaway || '',
          cta: infographic.cta || 'Visit canon.us/account for support',
        },
        sources: data.sources || [],
        confidence: data.confidence || 'medium',
        confidenceRationale: data.confidenceRationale || '',
        clarifyingQuestions: data.clarifyingQuestions || [],
      }

      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: assistantContent,
        response: supportResponse,
        timestamp: new Date(),
      }

      // Save to history if we have a good response
      const historyId = generateId()
      if (!hasClarifyingQuestions || data.confidence !== 'low') {
        const historyEntry: HistoryEntry = {
          id: historyId,
          query: content,
          response: supportResponse,
          timestamp: new Date(),
          responseTime,
          copiedPlatforms: [],
        }
        await addHistoryEntry(historyEntry)

        // Track analytics
        const analyticsEvent: AnalyticsEvent = {
          id: generateId(),
          type: 'query',
          confidence: supportResponse.confidence,
          responseTime,
          timestamp: new Date(),
        }
        await addAnalyticsEvent(analyticsEvent)
      }

      set((state) => ({
        messages: [...state.messages, assistantMessage],
        isLoading: false,
        currentHistoryId: historyId,
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred'

      const errorMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: `Sorry, there was an error: ${message}. Please try again.`,
        timestamp: new Date(),
      }

      set((state) => ({
        messages: [...state.messages, errorMessage],
        isLoading: false,
        error: message,
      }))
    }
  },

  clearChat: () =>
    set({
      messages: [],
      error: null,
      currentHistoryId: null,
    }),

  loadFromHistory: (entry) => {
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: entry.query,
      timestamp: entry.timestamp,
    }

    const assistantMessage: ChatMessage = {
      id: generateId(),
      role: 'assistant',
      content: `Confidence: ${entry.response.confidence}. ${entry.response.confidenceRationale || ''}`,
      response: entry.response,
      timestamp: entry.timestamp,
    }

    set({
      messages: [userMessage, assistantMessage],
      error: null,
      currentHistoryId: entry.id,
    })
  },
}))
